import * as XLSX from 'xlsx';
import { normalizeHeader, aliasSet } from './templateHeaders';
import { specColumnAliases, BENEFIT_HEADER_PATTERNS } from './templateSpecs';
// Bi-weekly payroll, step 4: the day a pay change may take effect on -- ONE owner (wall mirror M16 of the server's import).
import { raiseDayAllowed } from '../engines/paySchedule';

const r2 = n => Math.round(n * 100) / 100;

// ── Core column matchers ─────────────────────────────────────
/* THE HEADER ALIASES THIS PARSER READS -- the spec's own arrays (templateSpecs.js, the employee template), by
 * reference; the spec gate asserts it. They used to be hand-written regex lists here (EMP-1's Commission Frequency,
 * item 4b's raise pair and the two accepted misspellings of "Commission Rate" are all in the spec's lists). Matched
 * through the ONE header rule (templateHeaders.normalizeHeader) -- W-062 pass 2. */
export const EMPLOYEE_HEADER_COLUMNS = {
    'employee.Employee': specColumnAliases('employee', 'Employee'),
    'employee.Title': specColumnAliases('employee', 'Title'),
    'employee.Department': specColumnAliases('employee', 'Department'),
    'employee.IS Category': specColumnAliases('employee', 'IS Category'),
    'employee.Salary': specColumnAliases('employee', 'Salary'),
    'employee.Start Date': specColumnAliases('employee', 'Start Date'),
    'employee.State': specColumnAliases('employee', 'State'),
    'employee.Bonus': specColumnAliases('employee', 'Bonus'),
    'employee.Bonus Frequency': specColumnAliases('employee', 'Bonus Frequency'),
    'employee.Sales Quota': specColumnAliases('employee', 'Sales Quota'),
    'employee.Commission Rate': specColumnAliases('employee', 'Commission Rate'),
    'employee.Commission Frequency': specColumnAliases('employee', 'Commission Frequency'),
    'employee.New Salary': specColumnAliases('employee', 'New Salary'),
    'employee.Raise Date': specColumnAliases('employee', 'Raise Date'),
};
// field -> spec column, IN THE ORDER the old matcher tried them (the first match wins).
const CORE_FIELDS = [
    ['name', 'employee.Employee'], ['title', 'employee.Title'], ['department', 'employee.Department'],
    ['is_category', 'employee.IS Category'], ['annual_salary', 'employee.Salary'], ['start_date', 'employee.Start Date'],
    ['state', 'employee.State'], ['bonus_amount', 'employee.Bonus'], ['bonus_frequency', 'employee.Bonus Frequency'],
    ['annual_quota', 'employee.Sales Quota'], ['commission_rate', 'employee.Commission Rate'],
    ['commission_frequency', 'employee.Commission Frequency'], ['raise_new_salary', 'employee.New Salary'],
    ['raise_date', 'employee.Raise Date'],
].map(([field, key]) => [field, aliasSet(EMPLOYEE_HEADER_COLUMNS[key])]);

// ── Benefit column matchers ──────────────────────────────────
// EMP-3 / D-5: benefit columns are an EXPLICIT RECOGNIZED SET, never "any unmatched
// numeric column". Recognition is by HEADER NAME ALONE — a column's values never
// promote it to a benefit — so a stray numeric column (Employee ID, Headcount,
// FTE %, Raise) is structurally incapable of being summed into benefits.
// Unrecognized columns are IGNORED and reported (console + flags + ignoredColumns)
// so the opposite failure — a real benefit silently dropped — has a reader.
// The benefit pattern family is SPEC DATA (templateSpecs.BENEFIT_HEADER_PATTERNS, W-062 pass 2), read here by
// reference and applied AFTER the one header rule (normalizeHeader). Every header the old trim/lower-case patterns
// accepted is still accepted (the pass-2 superset check), plus hyphenated / punctuated spellings.
export const EMPLOYEE_BENEFIT_HEADER_PATTERNS = BENEFIT_HEADER_PATTERNS;
const BENEFIT_COLUMN_PATTERNS = EMPLOYEE_BENEFIT_HEADER_PATTERNS;

// Headers we accept but that are NOT the documented spelling — matched so the value is
// captured, reported so the template gets fixed. A warning with no reader is not a check.
// KEPT IN THE PARSER: these two are ACCEPTED through the spec's Commission Rate aliases, and additionally FLAGGED here
// (a flag is behaviour, not a match). Compared after the one header rule.
const MISSPELLED_RATE_HEADERS = [/^commision_rate$/, /^comission_rate$/];

function isMisspelledRateHeader(header) {
    const norm = normalizeHeader(header);
    return MISSPELLED_RATE_HEADERS.some(p => p.test(norm));
}

function isBenefitColumn(header) {
    if (!header) return false;
    const norm = normalizeHeader(header);
    return BENEFIT_COLUMN_PATTERNS.some(p => p.test(norm));
}

// ── Frequency normalization ──────────────────────────────────
// Canonical tokens the engine understands (computePayroll / compCycle FREQ_MONTHS).
// The template writes human spellings ("Semi annual", "Quarterly"); the DB and the
// anchor derivation need snake_case. Punctuation/space-insensitive by construction.
const FREQ_CANON = {
    monthly: 'monthly', month: 'monthly', mo: 'monthly',
    quarterly: 'quarterly', quarter: 'quarterly', qtr: 'quarterly',
    semiannual: 'semi_annual', semiannually: 'semi_annual', semi: 'semi_annual',
    biannual: 'semi_annual', biannually: 'semi_annual',
    annual: 'annual', annually: 'annual', yearly: 'annual', year: 'annual', yr: 'annual',
};

function normalizeFrequency(val) {
    const key = String(val == null ? '' : val).toLowerCase().replace(/[^a-z]/g, '');
    if (!key) return null;
    return FREQ_CANON[key] || null;
}

function matchCoreColumn(header) {
    if (!header) return null;
    const norm = normalizeHeader(header);
    for (const [field, aliases] of CORE_FIELDS) {
        if (aliases.has(norm)) return field;
    }
    return null;
}

function normalizeKey(s) {
    return String(s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/^(\d)/, 'k$1');
}

// ── Date parsing ─────────────────────────────────────────────
function parseExcelDate(val) {
    if (val == null || val === '') return null;
    if (typeof val === 'number') {
        // Excel serial date → Unix ms → Y/M/D via getUTC*
        const unixMs = (val - 25569) * 86400 * 1000;
        const d = new Date(unixMs);
        if (isNaN(d.getTime())) return null;
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${dd}`;
    }
    const s = String(val).trim();
    if (!s) return null;
    // ISO format YYYY-MM-DD
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
    if (iso) {
        const [, y, m, d] = iso;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    // MM/DD/YYYY or M/D/YY
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(s);
    if (us) {
        let [, m, d, y] = us;
        if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    // Fallback: YYYY/MM/DD
    const alt = /^(\d{4})\/(\d{1,2})\/(\d{1,2})/.exec(s);
    if (alt) {
        const [, y, m, d] = alt;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return null;
}

function parseNumber(val) {
    if (val == null || val === '') return 0;
    if (typeof val === 'number') return r2(val);
    const s = String(val).replace(/[$,()%]/g, '').trim();
    if (!s || s === '-') return 0;
    const isNeg = String(val).includes('(') && String(val).includes(')');
    const n = parseFloat(s);
    if (isNaN(n)) return 0;
    return r2(isNeg ? -Math.abs(n) : n);
}

function cleanString(val) {
    if (val == null) return '';
    return String(val).trim();
}

// ── Sheet detection ──────────────────────────────────────────
function pickEmployeeSheet(workbook) {
    const names = workbook.SheetNames;
    const preferred = names.find(n => /employee|roster|payroll|staff|headcount/i.test(n));
    return preferred || names[0];
}

// ── Main parser ──────────────────────────────────────────────
/* `options.payFrequency` (optional): the company's STORED pay frequency ('semi_monthly' | 'biweekly' | null). It decides ONE
   thing -- the day a pay change may take effect on (raiseDayAllowed). Omitted, null or 'semi_monthly' => the 1st or the 16th,
   every message word for word as before. */
export function parseEmployeeExcel(arrayBuffer, options) {
    const payFrequency = (options && options.payFrequency) || null;
    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: false });
    const sheetName = pickEmployeeSheet(workbook);
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) {
        return { employees: [], benefitColumns: [], departments: [], flags: [{ type: 'no_sheet', severity: 'error', scope: 'sheet', row: 0, message: 'No usable sheet found' }] };
    }

    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null });
    if (!rows.length) {
        return { employees: [], benefitColumns: [], departments: [], flags: [{ type: 'empty_sheet', severity: 'error', scope: 'sheet', row: 0, message: 'Sheet is empty' }] };
    }

    // ── Locate header row: first row with at least 2 recognizable core columns
    let headerRowIdx = 0;
    let headerFound = false;
    for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const row = rows[i] || [];
        let coreHits = 0;
        for (const cell of row) {
            if (matchCoreColumn(cell)) coreHits++;
        }
        if (coreHits >= 2) {
            headerRowIdx = i;
            headerFound = true;
            break;
        }
    }

    const headerRow = rows[headerRowIdx] || [];
    const dataRows = rows.slice(headerRowIdx + 1);

    // ── Classify columns
    // NOTE: `flags` is declared HERE, above its first use in the header-classification
    // loop below. Declaring it further down (with `employees`) put it in the temporal
    // dead zone for that loop and threw at parse time.
    const flags = [];
    const coreCols = {}; // field → colIdx
    const benefitCols = []; // { colIdx, headerName, normalizedKey }
    const unknownCols = []; // { colIdx, headerName }

    headerRow.forEach((cell, colIdx) => {
        const headerName = cleanString(cell);
        if (!headerName) return;
        const coreField = matchCoreColumn(headerName);
        if (coreField) {
            coreCols[coreField] = colIdx;
            if (isMisspelledRateHeader(headerName)) {
                flags.push({
                    type: 'misspelled_header', severity: 'warning', scope: 'column',
                    row: headerRowIdx + 1,
                    message: `Column "${headerName}" is misspelled — read as Commission Rate, but please correct the template header to "Commission Rate".`,
                });
            }
        } else {
            unknownCols.push({ colIdx, headerName });
        }
    });

    /* ★★ §E1 — A DELETED **REQUIRED** COLUMN IS A COLUMN-LEVEL FACT AND MUST BE SAID ONCE,
     * AT THE HEADER, NOT ONCE PER EMPLOYEE.
     *
     * Sibling of a9680f9's two notices in parseFinancialExcel / parseVendorExcel, and the
     * first of that shape on a column the template spec marks REQUIRED rather than optional.
     *
     * THE DEFECT IT CLOSES: header detection accepts any row with >=2 recognisable core
     * columns, so `Employee` + `Title` is a valid header and Salary can be absent ENTIRELY.
     * The per-row read below then does `coreCols.annual_salary != null ? … : 0` and every
     * employee imports at $0 — a roster that looks complete and forecasts no payroll.
     *
     * ⛔ THE PER-ROW "has no annual salary" WARNING BELOW IS NOT THE DEFECT AND IS UNTOUCHED.
     *   It is CORRECT for a row that genuinely has no salary in a workbook that HAS the
     *   column. What was wrong was the LEVEL: a 200-person roster produced 200 identical row
     *   warnings and never once named the missing column.
     *
     * ★ THE DISTINCTION THIS RELIES ON, and it is the whole reason the check is possible:
     *   `coreCols[field]` is assigned ONLY when a header cell matches, so
     *     column ABSENT            → `coreCols.annual_salary === undefined`
     *     column PRESENT, cells empty → `coreCols.annual_salary` is a colIdx (possibly 0)
     *   ⛔ Test with `== null`, NEVER falsy: Salary in column A is index 0, and a falsy test
     *     would report the column missing on a workbook that has it first.
     *   ⓘ That information is LOST one line later at the per-row read, which maps
     *     column-absent and cell-empty onto the same 0 — so this must be asked HERE OR NEVER.
     *     Exactly the collapse `canonCellText` causes in the canonical parsers. */
    if (coreCols.annual_salary == null) {
        flags.push({
            type: 'no_salary_column', severity: 'warning', scope: 'sheet',
            row: headerRowIdx + 1,
            label: 'Salary',
            message: 'This sheet has no Salary column, so every employee will import with an '
                + 'annual salary of $0 and the payroll forecast will be empty. Salary is a '
                + 'required column — re-download the template and re-upload with it filled in.',
        });
    }

    // Benefit columns = unknown columns whose HEADER is a recognized benefit name.
    // (EMP-3) Values do not participate: a recognized column that happens to be empty
    // is still a benefit ("a zero is data"), and an unrecognized column full of numbers
    // is still ignored. That asymmetry is the whole point — it makes misclassification
    // of a stray numeric column impossible rather than merely unlikely.
    const ignoredColumns = [];
    for (const col of unknownCols) {
        if (!isBenefitColumn(col.headerName)) {
            ignoredColumns.push(col.headerName);
            continue;
        }
        // Collect first 3 non-null values for the Step-5b confirmation sample.
        const sampleValues = [];
        for (const row of dataRows) {
            const v = row[col.colIdx];
            if (v != null && v !== '') {
                sampleValues.push(parseNumber(v));
                if (sampleValues.length >= 3) break;
            }
        }
        benefitCols.push({
            colIdx: col.colIdx,
            headerName: col.headerName,
            normalizedKey: normalizeKey(col.headerName),
            sampleValues,
        });
    }

    // ── Parse rows
    const employees = [];
    const departmentMap = new Map(); // name → { count, is_category }

    dataRows.forEach((row, rIdx) => {
        const rowNum = headerRowIdx + rIdx + 2; // 1-based with header
        const name = cleanString(row[coreCols.name]);
        if (!name) return; // skip blank rows

        const titleVal = coreCols.title != null ? cleanString(row[coreCols.title]) : '';
        const deptVal = coreCols.department != null ? cleanString(row[coreCols.department]) : '';
        const isCategoryVal = coreCols.is_category != null ? cleanString(row[coreCols.is_category]) : '';
        const salaryVal = coreCols.annual_salary != null ? parseNumber(row[coreCols.annual_salary]) : 0;
        const startDateRaw = coreCols.start_date != null ? row[coreCols.start_date] : null;
        const stateVal = coreCols.state != null ? cleanString(row[coreCols.state]).toUpperCase().slice(0, 2) : '';
        const bonusAmt = coreCols.bonus_amount != null ? parseNumber(row[coreCols.bonus_amount]) : 0;
        // Frequencies are normalized to the engine's canonical tokens. The raw template
        // spelling ("Semi annual") is NOT a valid token — passing it through stored a
        // value FREQ_MONTHS cannot look up, so the anchor derivation silently no-ops.
        const bonusFreqRaw = coreCols.bonus_frequency != null ? cleanString(row[coreCols.bonus_frequency]) : '';
        const bonusFreq = normalizeFrequency(bonusFreqRaw);
        if (bonusFreqRaw && !bonusFreq) {
            flags.push({ type: 'unknown_bonus_frequency', severity: 'warning', scope: 'row', row: rowNum, message: `${name}: unrecognized bonus frequency "${bonusFreqRaw}" — left unset.` });
        }
        const commFreqRaw = coreCols.commission_frequency != null ? cleanString(row[coreCols.commission_frequency]) : '';
        const commFreq = normalizeFrequency(commFreqRaw);
        if (commFreqRaw && !commFreq) {
            flags.push({ type: 'unknown_commission_frequency', severity: 'warning', scope: 'row', row: rowNum, message: `${name}: unrecognized commission frequency "${commFreqRaw}" — left unset.` });
        }
        const quotaVal = coreCols.annual_quota != null ? parseNumber(row[coreCols.annual_quota]) : 0;
        // Commission rate accepts BOTH conventions and always emits a DECIMAL fraction:
        //   0.08  -> 0.08      (already a fraction: any value <= 1 is taken as-is)
        //   8     -> 0.08      (a value > 1 is read as whole percent and divided by 100)
        //   "8%"  -> 0.08      (parseNumber strips the % sign, then the > 1 rule applies)
        // Rounded to 4dp, NOT r2: r2 would collapse 8.5% (0.085) to 0.09 — a 0.5pt error
        // on every commission dollar. 4dp preserves basis-point rates and still kills float drift.
        let commRate = coreCols.commission_rate != null ? parseNumber(row[coreCols.commission_rate]) : 0;
        if (commRate > 1) commRate = commRate / 100;
        commRate = Math.round(commRate * 10000) / 10000;

        const startDate = parseExcelDate(startDateRaw);
        if (startDateRaw != null && startDateRaw !== '' && !startDate) {
            flags.push({ type: 'unparseable_start_date', severity: 'warning', scope: 'row', row: rowNum, message: `Could not parse start date for ${name}: "${startDateRaw}"` });
        }
        if (!salaryVal) {
            flags.push({ type: 'missing_salary', severity: 'warning', scope: 'row', row: rowNum, message: `${name} has no annual salary` });
        }

        /* ★★ ITEM 4b — THE RAISE PAIR. Read TOGETHER; either both land or neither does.
         * ⛔ NO SILENT DEFAULT ANYWHERE (§E1): every rejection below names the employee in a flag,
         *   because the failure this closes is a SILENT one — an uncaptured raise leaves a flat
         *   salary series that P7 then re-derives from the same empty input and passes.
         * ⛔ THE 1st/16th RULE IS THE PAYROLL MODAL'S CONSTRAINT, NOT THE ENGINE'S. buildMonthlyData
         *   branches only on `day >= 16` and handles a 9th perfectly well (measured: identical
         *   series to the 1st). The COMPENSATION HISTORY editor, however, offers only the 1st and
         *   the 16th: it renders any other day as the 1st and rewrites it on the next save. An
         *   off-cycle raise would import correctly and then silently move. ⇒ reject it here.
         *   Do NOT relax this to match the engine — the engine is not the reference implementation.
         * BI-WEEKLY (step 4, 2026-10-08; Brian B3): a bi-weekly company's pay change takes effect on ANY real date -- its
         *   editor is a date input and its derivation splits the pay period on the date. The rule has ONE owner,
         *   paySchedule.raiseDayAllowed (the server's import applies its mirror, M16). A company that is semi-monthly or was
         *   never asked keeps the 1st / 16th rule and every message below, word for word. */
        let raise = null;
        const raiseSalRaw = coreCols.raise_new_salary != null ? row[coreCols.raise_new_salary] : null;
        const raiseDateRaw = coreCols.raise_date != null ? row[coreCols.raise_date] : null;
        const hasRaiseSal = raiseSalRaw != null && String(raiseSalRaw).trim() !== '';
        const hasRaiseDate = raiseDateRaw != null && String(raiseDateRaw).trim() !== '';
        if (hasRaiseSal || hasRaiseDate) {
            // one side without the other is an operator mistake, never an inferable intent
            if (!hasRaiseSal || !hasRaiseDate) {
                const missing = hasRaiseSal ? 'Raise Date' : 'New Salary';
                const present = hasRaiseSal ? 'New Salary' : 'Raise Date';
                flags.push({ type: 'incomplete_raise', severity: 'warning', scope: 'row', row: rowNum,
                    message: `${name} has ${present} but no ${missing} — the pay change was NOT imported. Fill both, or clear both.` });
            } else {
                // REUSE the file's ONE date vocabulary — ⛔ never a second parser
                const rDate = parseExcelDate(raiseDateRaw);
                const rSal = parseNumber(raiseSalRaw);
                if (!rDate) {
                    flags.push({ type: 'unparseable_raise_date', severity: 'warning', scope: 'row', row: rowNum,
                        message: `Could not read the Raise Date for ${name}: "${raiseDateRaw}" — the pay change was NOT imported. ${payFrequency === 'biweekly' ? 'Use a real calendar date.' : 'Use a real date on the 1st or the 16th.'}` });
                } else if (rSal <= 0) {
                    flags.push({ type: 'invalid_raise_salary', severity: 'warning', scope: 'row', row: rowNum,
                        message: `${name} has a New Salary of "${raiseSalRaw}" — the pay change was NOT imported. It must be a positive annual salary.` });
                } else {
                    const rDay = parseInt(rDate.split('-')[2], 10);
                    /* ONE RULE, ASKED TWO WAYS. A bi-weekly date must be a REAL date exactly as read. The semi-monthly rule is
                       about the DAY alone, and raiseDayAllowed reads the day from an ISO date's fixed position -- but
                       parseExcelDate can hand back a year that is not four digits (a typo such as 1/16/202), which would
                       shift that position. So the semi-monthly question is asked of the day this parser already read: the
                       decision is then exactly the old `rDay !== 1 && rDay !== 16`, in every case. */
                    const dayRule = payFrequency === 'biweekly'
                        ? raiseDayAllowed('biweekly', rDate)
                        : raiseDayAllowed(payFrequency, '2000-01-' + String(rDay).padStart(2, '0'));
                    if (dayRule === 'NOT_1ST_OR_16TH') {
                        flags.push({ type: 'raise_date_not_semimonthly', severity: 'warning', scope: 'row', row: rowNum,
                            message: `${name}'s Raise Date ${rDate} falls on day ${rDay} — the pay change was NOT imported. Payroll runs semi-monthly, so a pay change must start on the 1st or the 16th.` });
                    } else if (dayRule) {
                        // bi-weekly: the cell read as a date that does not exist (a 31st of a 30-day month)
                        flags.push({ type: 'unparseable_raise_date', severity: 'warning', scope: 'row', row: rowNum,
                            message: `Could not read the Raise Date for ${name}: "${raiseDateRaw}" — the pay change was NOT imported. Use a real calendar date.` });
                    } else {
                        raise = { effective_date: rDate, new_salary: rSal };
                    }
                }
            }
        }
        // ★ A BLANK DEPARTMENT IS THE WIDEST ROUTE TO PAYROLL THAT NEVER REACHES THE P&L,
        // and until now it was the ONLY core field with no flag. The row is still stored
        // (department: null below) — this WARNS, it does not reject. It matters because the
        // department list this parser returns is built only from NON-blank cells, so a blank
        // one never appears at Step 5c, the all-mapped gate is satisfied, and the employee
        // is imported with a NULL department that resolves to no is_category. The engine
        // now names that too (orphanDeptWarnings), but this catches it one step earlier —
        // on the upload review screen, before the tenant is saved.
        if (!deptVal) {
            flags.push({ type: 'missing_department', severity: 'warning', scope: 'row', row: rowNum, message: `${name} has no department — their payroll will not reach the P&L.` });
        }
        // A sales rep (quota > 0) with no commission rate earns $0 commission — and an absent
        // or misnamed rate column produces exactly that, silently. Make it loud.
        if (quotaVal > 0 && !(commRate > 0)) {
            flags.push({ type: 'missing_commission_rate', severity: 'warning', scope: 'row', row: rowNum, message: `${name} has a sales quota but no commission rate — commissions will be $0.` });
        }

        // Benefit amounts
        const benefitAmounts = {};
        for (const col of benefitCols) {
            const amt = parseNumber(row[col.colIdx]);
            benefitAmounts[col.normalizedKey] = amt;
        }

        employees.push({
            name,
            title: titleVal || null,
            department: deptVal || null,
            is_category: isCategoryVal || null,
            annual_salary: salaryVal,
            start_date: startDate,
            state: stateVal && /^[A-Z]{2}$/.test(stateVal) ? stateVal : null,
            bonus_amount: bonusAmt,
            bonus_frequency: bonusFreq || null,
            annual_quota: quotaVal,
            commission_rate: commRate,
            commission_frequency: commFreq || null,
            benefitAmounts,
            // ★ item 4b: null when the row carries no usable pay change. ONE raise per employee.
            raise,
        });

        if (deptVal) {
            const existing = departmentMap.get(deptVal) || { name: deptVal, count: 0, is_category: null };
            existing.count += 1;
            if (!existing.is_category && isCategoryVal) existing.is_category = isCategoryVal;
            departmentMap.set(deptVal, existing);
        }
    });

    const departments = [...departmentMap.values()];

    // EMP-3: an ignored column is the FALSE-NEGATIVE direction (a real benefit dropped),
    // so it gets a reader rather than silence — console note + a flag + the returned list.
    if (ignoredColumns.length) {
        // eslint-disable-next-line no-console
        console.warn(`[parseEmployeeExcel] ignored ${ignoredColumns.length} unrecognized column(s), not treated as benefits: ${ignoredColumns.join(', ')}`);
        flags.push({
            type: 'ignored_columns', severity: 'info', scope: 'sheet',
            row: headerRowIdx + 1,
            message: `Ignored ${ignoredColumns.length} unrecognized column(s): ${ignoredColumns.join(', ')}. If any of these is a benefit, add it with "+ Add Benefit Component".`,
        });
    }

    /* W-068 -- what the template check reads: this parser's header->column map under the SPEC's column names, the
     * benefit columns (numbers, matched by the benefit header family) and the raw rows (row i is spreadsheet row i + 1,
     * the numbering of this parser's own flags). Absent when no header row was recognised (0 employees -> Step 5
     * refuses the file loudly already). */
    const SPEC_NAME = { name: 'Employee', title: 'Title', department: 'Department', is_category: 'IS Category', annual_salary: 'Salary',
        start_date: 'Start Date', state: 'State', bonus_amount: 'Bonus', bonus_frequency: 'Bonus Frequency', annual_quota: 'Sales Quota',
        commission_rate: 'Commission Rate', commission_frequency: 'Commission Frequency', raise_new_salary: 'New Salary', raise_date: 'Raise Date' };
    const specScan = headerFound ? {
        template: 'employee', layout: 'template', headerRow: headerRowIdx,
        cols: Object.fromEntries(Object.entries(SPEC_NAME).map(([field, colName]) => [colName, coreCols[field] ?? null])),
        monthCols: {}, extraNumberCols: benefitCols.map(c => ({ col: c.colIdx, name: c.headerName })), rows,
    } : null;

    return {
        employees,
        benefitColumns: benefitCols.map(c => ({
            headerName: c.headerName,
            normalizedKey: c.normalizedKey,
            sampleValues: c.sampleValues,
        })),
        departments,
        ignoredColumns,
        flags,
        ...(specScan ? { specScan } : {}),
    };
}

export default parseEmployeeExcel;
